import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  AccessMapResponse,
  accessMapResponseSchema,
  FindingsFiltersSuccessResponse,
  findingsFiltersSuccessResponseSchema,
  FindingsRequestPayload,
  PolicyFindingsResponse,
  policyFindingsResponseSchema,
  PolicyFindingsTicketActionPayload,
  PolicyFindingsTicketActionResponse,
  policyFindingsTicketActionResponseSchema,
} from "../schemas/policiesFindings.schema";
import { getHashedUsername } from "./policiesSaveFilterView.api";

const policiesClient = createAuthenticatedClient(`${API_BASE_URL}/ispm/v1`);
const policiesNHIClient = createAuthenticatedClient(`${API_BASE_URL}/nhi`);

export const policiesFindingsApi = {
  getPolicyFindingsList: async ({
    payload,
    offset,
    limit,
  }: {
    payload?: FindingsRequestPayload;
    offset?: number;
    limit?: number;
  }): Promise<PolicyFindingsResponse> => {
    const data = await policiesClient.post<unknown>("/finding/violations", {
      offset,
      limit,
      ...payload,
    });
    const validated: PolicyFindingsResponse = safeValidate(
      policyFindingsResponseSchema,
      data,
      "getPolicyFindingsList",
    ) as PolicyFindingsResponse;
    return validated;
  },
  getPolicyFindingsFilters:
    async (): Promise<FindingsFiltersSuccessResponse> => {
      const data = await policiesClient.get<unknown>("/finding/filters");
      const validated: FindingsFiltersSuccessResponse = safeValidate(
        findingsFiltersSuccessResponseSchema,
        data,
        "getpolicyFindingsFilters",
        true, // preserveExtra to keep any additional fields that might be returned by the API but not defined in the schema
      ) as FindingsFiltersSuccessResponse;
      // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
      // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
      return validated;
    },
  policyFindingsTicketAction: async (
    payload: PolicyFindingsTicketActionPayload,
  ): Promise<PolicyFindingsTicketActionResponse> => {
    const data = await policiesNHIClient.post<unknown>(
      "/v1/remediation",
      payload,
    );
    const validated: PolicyFindingsTicketActionResponse = safeValidate(
      policyFindingsTicketActionResponseSchema,
      data,
      "policyFindingsTicketAction",
    ) as PolicyFindingsTicketActionResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  bulkTicketCreation: async (payload: unknown) =>
    await policiesNHIClient.post<unknown>("/v1/remediation/tickets", payload),
  createTicket: async (violationId: string) => {
    const hashedUsername = getHashedUsername();
    return await policiesNHIClient.post<unknown>(
      "/v1/remediation/ticket",
      {
        violationId,
      },
      { headers: { username: hashedUsername } },
    );
  },
  getAccessMap: async (id: string): Promise<AccessMapResponse> => {
    const data = await policiesNHIClient.get<AccessMapResponse>(
      `/v2/asset/${id}/accessMap`,
    );
    const validated: AccessMapResponse = safeValidate(
      accessMapResponseSchema,
      data,
      "getAccessMap",
    ) as AccessMapResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
};
